import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container, Typography, Box, Grid,
  Button, CircularProgress, Chip, TextField, Dialog, DialogTitle,
  DialogContent, DialogActions,
} from '@mui/material';
import StorefrontIcon from '@mui/icons-material/Storefront';
import ShoppingBagIcon from '@mui/icons-material/ShoppingBag';
import coursesService from '../../services/courses.service';
import userService from '../../services/user.service';

/* ── Design tokens ─────────────────────────────────────────── */
const T = {
  purple: '#552583', purpleDark: '#3d1a61', gold: '#FDB927',
  bg: '#E4F0F6', white: '#ffffff', ink: '#1a1a1a', sub: '#4a4a4a', muted: '#7a7a7a',
  border: 'rgba(85,37,131,0.08)',
  radius: { sm: '8px', md: '10px', btn: '12px', card: '20px' },
  transition: '0.15s ease',
  shadow: '0 1px 4px rgba(0,0,0,0.06)',
};

const CoursesList = () => {
  const navigate = useNavigate();
  const [stores, setStores] = useState([]);
  const [addresses, setAddresses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedStore, setSelectedStore] = useState(null);
  const [shoppingList, setShoppingList] = useState('');
  const [selectedAddress, setSelectedAddress] = useState('');

  useEffect(() => {
    Promise.all([
      coursesService.getStores(),
      userService.getAddresses()
    ]).then(([storesData, addressesData]) => {
      setStores(storesData.stores || []);
      setAddresses(addressesData.addresses || []);
      const defaultAddr = addressesData.addresses?.find(a => a.is_default);
      if (defaultAddr) setSelectedAddress(defaultAddr.id);
    }).finally(() => setLoading(false));
  }, []);

  const handleOrder = async () => {
    if (!shoppingList.trim() || !selectedAddress) return;
    try {
      await coursesService.createOrder({
        store_id: selectedStore.id,
        delivery_address_id: selectedAddress,
        shopping_list: shoppingList,
      });
      navigate('/courses/orders');
    } catch (error) {
      console.error(error);
    }
  };

  if (loading) return (
    <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
      <CircularProgress sx={{ color: T.purple }} size={30} />
    </Box>
  );

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: T.bg }}>
      <Container maxWidth="lg" sx={{ pt: 4, pb: 6 }}>

        {/* Header */}
        <Box sx={{ mb: 3.5 }}>
          <Typography sx={{ fontWeight: 800, color: T.ink, fontSize: '1.5rem', letterSpacing: '-0.01em' }}>
            Magasins disponibles
          </Typography>
          <Typography sx={{ color: T.muted, mt: 0.3, fontSize: '0.85rem' }}>
            Choisissez un magasin et faites votre liste de courses
          </Typography>
        </Box>

        {/* Store grid */}
        {stores.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <StorefrontIcon sx={{ fontSize: 48, color: 'rgba(85,37,131,0.12)', mb: 2 }} />
            <Typography sx={{ fontWeight: 600, color: T.sub, mb: 0.5 }}>Aucun magasin disponible</Typography>
            <Typography sx={{ color: T.muted, fontSize: '0.85rem' }}>Revenez bientôt, de nouveaux partenaires arrivent.</Typography>
          </Box>
        )}

        <Grid container spacing={2}>
          {stores.map((store) => (
            <Grid item xs={12} sm={6} md={4} key={store.id}>
              <Box sx={{
                p: 2.5, bgcolor: T.white, borderRadius: T.radius.card,
                border: `1px solid ${T.border}`, boxShadow: T.shadow,
                transition: T.transition, display: 'flex', flexDirection: 'column', height: '100%',
                '&:hover': { borderColor: 'rgba(85,37,131,0.15)', boxShadow: '0 3px 12px rgba(0,0,0,0.08)' },
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2, mb: 1.5 }}>
                  <Box sx={{
                    width: 38, height: 38, borderRadius: T.radius.md,
                    bgcolor: 'rgba(85,37,131,0.05)', border: `1px solid ${T.border}`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <StorefrontIcon sx={{ fontSize: 20, color: T.purple }} />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography sx={{ fontWeight: 700, color: T.ink, fontSize: '0.95rem' }}>{store.name}</Typography>
                  </Box>
                </Box>

                <Chip label={store.store_type} size="small"
                  sx={{
                    alignSelf: 'flex-start', mb: 1, height: 22,
                    bgcolor: 'rgba(85,37,131,0.04)', color: T.purple,
                    border: `1px solid ${T.border}`, fontSize: '0.68rem', fontWeight: 600,
                    borderRadius: '6px',
                  }}
                />

                <Typography sx={{ color: T.sub, fontSize: '0.82rem', lineHeight: 1.5, mb: 2, flex: 1 }}>
                  {store.address}
                </Typography>

                <Button fullWidth variant="contained"
                  startIcon={<ShoppingBagIcon sx={{ fontSize: '0.9rem !important' }} />}
                  onClick={() => { setSelectedStore(store); setDialogOpen(true); }}
                  sx={{
                    bgcolor: T.purple, color: 'white', fontWeight: 600,
                    borderRadius: T.radius.btn, textTransform: 'none', fontSize: '0.85rem',
                    boxShadow: 'none', transition: T.transition, py: 0.9,
                    '&:hover': { bgcolor: T.purpleDark, boxShadow: 'none' },
                  }}
                >
                  Commander
                </Button>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Order dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth
        PaperProps={{ sx: { borderRadius: T.radius.card, bgcolor: T.white } }}>
        <DialogTitle sx={{ fontWeight: 700, color: T.ink, fontSize: '1.05rem', pb: 0.5 }}>
          Faire mes courses
          {selectedStore && (
            <Typography sx={{ color: T.muted, fontSize: '0.78rem', fontWeight: 400, mt: 0.3 }}>
              {selectedStore.name}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <TextField
            fullWidth label="Liste de courses" multiline rows={5}
            value={shoppingList} onChange={(e) => setShoppingList(e.target.value)}
            placeholder="Ex: 2x Lait (1L)&#10;1x Pain&#10;500g Viande..."
            sx={{ mt: 1.5 }}
          />
          <TextField
            select fullWidth label="Adresse de livraison"
            value={selectedAddress} onChange={(e) => setSelectedAddress(e.target.value)}
            SelectProps={{ native: true }}
            sx={{ mt: 2 }}
          >
            <option value="">Sélectionner</option>
            {addresses.map((addr) => (
              <option key={addr.id} value={addr.id}>
                {addr.label} - {addr.address_line1}
              </option>
            ))}
          </TextField>
        </DialogContent>
        <DialogActions sx={{ p: 2.5, pt: 0, gap: 1 }}>
          <Button onClick={() => setDialogOpen(false)}
            sx={{ color: T.muted, textTransform: 'none', fontWeight: 500, borderRadius: T.radius.sm, fontSize: '0.85rem' }}>
            Annuler
          </Button>
          <Button onClick={handleOrder} variant="contained"
            disabled={!shoppingList.trim() || !selectedAddress}
            sx={{
              bgcolor: T.purple, color: 'white', fontWeight: 700,
              borderRadius: T.radius.btn, textTransform: 'none', px: 3, fontSize: '0.85rem',
              boxShadow: 'none', transition: T.transition,
              '&:hover': { bgcolor: T.purpleDark, boxShadow: 'none' },
              '&:disabled': { bgcolor: 'rgba(85,37,131,0.12)', color: T.muted },
            }}>
            Confirmer
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CoursesList;
